import { useJobsData } from "@/data/AppDataContext";
import { useJobsQuery } from "@/hooks/useJobsQuery";
import { JobsSkeleton } from "@/components/ui/jobs-skeleton";
import { Link, useNavigate } from "react-router-dom";
import { EmptyState } from "@/components/EmptyState";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Search, CheckCircle2, AlertCircle, Clock, Briefcase,
  AlertTriangle, PackageX, FileWarning, Users, ClipboardCheck,
  ArrowRight, ShieldCheck, Target, XCircle, TrendingUp,
} from "lucide-react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";
import { useWindowedList } from "@/hooks/useWindowedList";
import { getJobBlockers, type Blocker } from "@/data/workflowBlockers";
import { getJobSuggestedActions, type SuggestedAction } from "@/data/suggestedActions";
import { getJobNextAction } from "@/data/nextActionEngine";
import { getLastActivity, formatRelativeTime } from "@/data/suggestedActions";
import { BranchRegionFilter, EMPTY_BR, filterByBranchRegion, type BranchRegionState } from "@/components/BranchRegionFilter";
import { QuickViews, type QuickView } from "@/components/QuickViews";
import { formatAddress } from "@/lib/addressUtils";
import { StatusBadge } from "@/components/StatusBadge";
import { ReadinessBadge } from "@/components/ReadinessBadge";
import { OrderingModal } from "@/components/OrderingModal";
import { RescheduleModal } from "@/components/quick-actions/RescheduleModal";
import { QuickAssignModal } from "@/components/quick-actions/QuickAssignModal";
import { QuickResolveIssueModal } from "@/components/quick-actions/QuickResolveIssueModal";
import { runAction, type ExecutableAction } from "@/lib/actionRunner";
import { usePushChain } from "@/hooks/usePushChain";
import { JobService } from "@/services/job.service";
import { toast } from "sonner";
import type { Job } from "@/data/types";

// ── Progress Calculation ──

interface JobProgress {
  percent: number;
  label: string;
  status: "blocked" | "in_progress" | "ready" | "completed";
  milestones: { label: string; done: boolean }[];
}

function calculateJobProgress(job: import("@/data/types").Job, blockers: Blocker[]): JobProgress {
  if (job.currentStage === "Completed") {
    return { percent: 100, label: "Completed", status: "completed", milestones: [] };
  }

  const permitDone = !job.permitRequired || job.permitStatus === "Approved";
  const materialsDone = job.materialReadiness === "Fully Received" || job.materialReadiness === "Loaded for Crew";
  const crewDone = job.assignmentType !== "Unassigned";
  const qaDone = !!job.qaSubmitted;

  const milestones = [
    { label: "Permit", done: permitDone },
    { label: "Materials", done: materialsDone },
    { label: "Crew", done: crewDone },
    { label: "QA", done: qaDone },
  ];

  const doneCount = milestones.filter(m => m.done).length;
  const percent = Math.round((doneCount / 4) * 100);

  const hasCritical = blockers.some(b => b.severity === "critical");

  let status: JobProgress["status"] = "in_progress";
  let label = "In Progress";

  if (hasCritical) {
    status = "blocked";
    label = "Blocked";
  } else if (percent === 100) {
    status = "ready";
    label = "Ready";
  } else if (percent >= 75) {
    label = "Almost Ready";
  }

  return { percent, label, status, milestones };
}

// ── Next Action derivation ──
// Single source of truth: getJobNextAction (deterministic priority bucket sort).
// Local function only maps the engine result to a row icon.

function getNextAction(
  job: import("@/data/types").Job,
  openIssues: import("@/data/types").Issue[],
): { action: SuggestedAction; icon: typeof ArrowRight } | null {
  if (job.currentStage === "Completed") return null;
  const a = getJobNextAction(job, openIssues);
  if (!a) return null;
  const ICON_MAP: Record<string, typeof ArrowRight> = {
    crew: Users, materials: PackageX, permit: FileWarning,
    schedule: Clock, dispatch: ArrowRight, issue: AlertTriangle,
    qa: ClipboardCheck, closeout: CheckCircle2,
  };
  return { action: a, icon: ICON_MAP[a.icon] || ArrowRight };
}

// ── Blocker icons ──

const BLOCKER_ICONS: Record<string, typeof AlertTriangle> = {
  permit: FileWarning, materials: PackageX, issues: AlertCircle,
  qa: ClipboardCheck, contractor: Users, dispatch: ArrowRight,
};

// ── Component ──

export default function JobsList() {
  const { issues = [], setJobs } = useJobsData();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [activeView, setActiveView] = useState("active");
  const [brFilter, setBrFilter] = useState<BranchRegionState>(EMPTY_BR);
  const [tableScrollTop, setTableScrollTop] = useState(0);
  const TABLE_VIEWPORT_HEIGHT = 680;
  const TABLE_ROW_HEIGHT = 64;

  // Use database query instead of mock data
  const { jobs: dbJobs, loading } = useJobsQuery({ view: activeView, search });

  // Show loading skeleton while fetching data
  if (loading || !dbJobs) {
    return <JobsSkeleton />;
  }

  const jobs = dbJobs;

  /** Resolve top open issue for a job — used when chaining INTO Resolve. */
  const findOpenIssueForJob = useCallback(
    (jobId: string) => {
      const oi = issues.filter(
        (i) => i.jobId === jobId && (i.status === "Open" || i.status === "In Progress"),
      );
      return oi.sort((a, b) => (a.severity === "Urgent" ? -1 : 1) - (b.severity === "Urgent" ? -1 : 1))[0] ?? null;
    },
    [issues],
  );

  /** Single source of truth for which push modal is open + chain transitions. */
  const chain = usePushChain({ navigate, findOpenIssueForJob });

  /** Resolve a SuggestedAction into an ExecutableAction routed through the shared shim. */
  const handleActionClick = useCallback(
    async (job: Job, action: SuggestedAction) => {
      // Modal-first dispatch by action icon
      let executable: ExecutableAction;
      if (action.executionMode === "modal" && action.icon === "materials") {
        executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => chain.openOrdering(job) };
      } else if (action.executionMode === "modal" && action.icon === "schedule") {
        executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => chain.openReschedule(job) };
      } else if (action.executionMode === "modal" && (action.icon === "dispatch" || action.icon === "crew")) {
        executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => chain.openAssign(job) };
      } else if (action.executionMode === "modal" && action.icon === "issue") {
        executable = {
          label: action.cta || action.label,
          executionMode: "modal",
          openModal: () => {
            const top = findOpenIssueForJob(job.id);
            if (top) chain.openResolve(top);
            else toast.info("No open issue to resolve");
          },
        };
      } else {
        executable = { label: action.cta || action.label, executionMode: "navigate", route: action.link || `/jobs/${job.id}` };
      }
      const handled = await runAction(executable, { navigate });
      if (!handled && action.link) navigate(action.link); // safety fallback
    },
    [navigate, chain, findOpenIssueForJob],
  );

  const handleOrderingSubmit = useCallback(
    async (data: { jobId: string; vendor: string; orderType: "Batch" | "Direct" | "Rush"; notes: string }) => {
      const target = jobs.find((j) => j.id === data.jobId);
      if (!target) return;
      const patch: Partial<Job> = {
        materialReadiness: "Ordered" as any,
        planningStatus: "Ordered",
        selectedVendor: data.vendor,
        activityHistory: [
          ...target.activityHistory,
          {
            id: `jobs-order-${Date.now()}`,
            date: new Date().toISOString(),
            action: `Materials ordered — ${data.vendor} (${data.orderType})`,
            user: "Ops",
            noteType: "ops" as const,
          },
        ],
      };
      const { data: persisted, error } = await JobService.update(target.id, patch);
      if (error || !persisted) {
        console.error("Failed to persist ordering update:", error);
        toast.error("Failed to place order");
        return;
      }
      setJobs((prev) => prev.map((j) => (j.id === persisted.id ? persisted : j)));
      toast.success("Order placed", { description: `${data.vendor} — ${data.orderType}` });
      // NOTE: do NOT close the modal here and do NOT fire suggestNextStep toast —
      // the OrderingModal renders an inline PushNextStepPanel showing the next
      // action persistently. Toast + panel = conflicting signals.
    },
    [jobs, setJobs],
  );

  const brFiltered = filterByBranchRegion(jobs, brFilter);

  // Precompute per-job data
  const jobData = useMemo(() => {
    const map = new Map<string, {
      openIssues: import("@/data/types").Issue[];
      blockers: Blocker[];
      progress: JobProgress;
      nextAction: ReturnType<typeof getNextAction>;
    }>();
    brFiltered.forEach(j => {
      const oi = issues.filter(i => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"));
      const blockers = j.currentStage !== "Completed" ? getJobBlockers(j, oi) : [];
      const progress = calculateJobProgress(j, blockers);
      const nextAction = getNextAction(j, oi);
      map.set(j.id, { openIssues: oi, blockers, progress, nextAction });
    });
    return map;
  }, [brFiltered, issues]);

  const active = brFiltered.filter(j => j.currentStage !== "Completed");
  const blocked = active.filter(j => jobData.get(j.id)?.progress.status === "blocked");
  const ready = active.filter(j => jobData.get(j.id)?.progress.status === "ready");
  const needsAttention = active.filter(j => {
    const d = jobData.get(j.id);
    return d && d.blockers.some(b => b.severity === "critical");
  });
  const completed = brFiltered.filter(j => j.currentStage === "Completed");

  const views: QuickView[] = [
    { key: "all", label: "All", count: brFiltered.length },
    { key: "active", label: "Active", count: active.length },
    { key: "attention", label: "Needs Attention", count: needsAttention.length, accent: "destructive" },
    { key: "blocked", label: "Blocked", count: blocked.length, accent: "warning" },
    { key: "ready", label: "Ready", count: ready.length, accent: "success" },
    { key: "completed", label: "Completed", count: completed.length, accent: "default" },
  ];

  const viewFiltered = brFiltered.filter(j => {
    switch (activeView) {
      case "active": return j.currentStage !== "Completed";
      case "attention": return needsAttention.includes(j);
      case "blocked": return blocked.includes(j);
      case "ready": return ready.includes(j);
      case "completed": return j.currentStage === "Completed";
      default: return true;
    }
  });

  const filtered = viewFiltered.filter(j =>
    j.customerName.toLowerCase().includes(search.toLowerCase()) ||
    j.id.toLowerCase().includes(search.toLowerCase()) ||
    j.projectType.toLowerCase().includes(search.toLowerCase()) ||
    j.currentStage.toLowerCase().includes(search.toLowerCase())
  );

  const pagination = usePagination(filtered, 50);
  useEffect(() => {
    pagination.reset();
    setTableScrollTop(0);
  }, [search, activeView]);
  const rowWindow = useWindowedList(pagination.paged, {
    itemHeight: TABLE_ROW_HEIGHT,
    viewportHeight: TABLE_VIEWPORT_HEIGHT,
    scrollTop: tableScrollTop,
    overscan: 8,
  });

  // Summary KPIs
  const pctReady = active.length > 0 ? Math.round((ready.length / active.length) * 100) : 0;
  const atRisk = active.filter(j => {
    const p = jobData.get(j.id)?.progress;
    return p && p.percent > 0 && p.percent < 75 && p.status !== "blocked";
  }).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-lg font-bold tracking-tight">Jobs</h1>
        <p className="text-xs text-muted-foreground">{active.length} active · {blocked.length} blocked · {completed.length} completed</p>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KpiCard label="Ready for Install" value={`${pctReady}%`} sub={`${ready.length} jobs`} icon={CheckCircle2} color="text-success bg-success/10 border-success/20" />
        <KpiCard label="Blocked" value={blocked.length} sub="critical issues" icon={XCircle} color="text-destructive bg-destructive/10 border-destructive/20" />
        <KpiCard label="At Risk" value={atRisk} sub="partial progress" icon={AlertTriangle} color="text-warning bg-warning/10 border-warning/20" />
        <KpiCard label="Active" value={active.length} sub="in pipeline" icon={Target} color="text-primary bg-primary/10 border-primary/20" />
      </div>

      <QuickViews views={views} active={activeView} onChange={setActiveView} />

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search by customer, job ID, or type..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-8 text-xs"
          />
        </div>
        <BranchRegionFilter value={brFilter} onChange={setBrFilter} jobs={jobs} compact />
      </div>

      {/* Table */}
      <Card className="shadow-none">
        <CardContent className="p-0">
          <div
            className="overflow-x-auto overflow-y-auto"
            style={{ maxHeight: TABLE_VIEWPORT_HEIGHT }}
            onScroll={(event) => setTableScrollTop(event.currentTarget.scrollTop)}
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="text-left py-2.5 px-4 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Job</th>
                  <th className="text-left py-2.5 px-4 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Customer</th>
                  <th className="text-left py-2.5 px-4 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Stage</th>
                  <th className="text-left py-2.5 px-4 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider min-w-[160px]">Progress</th>
                  <th className="text-left py-2.5 px-4 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden md:table-cell">Blockers</th>
                  <th className="text-left py-2.5 px-4 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden md:table-cell">Next Action</th>
                  <th className="text-left py-2.5 px-4 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden lg:table-cell">Assigned</th>
                  <th className="text-left py-2.5 px-4 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden lg:table-cell">Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {rowWindow.topSpacerHeight > 0 && (
                  <tr aria-hidden>
                    <td colSpan={8} style={{ height: rowWindow.topSpacerHeight, padding: 0, border: 0 }} />
                  </tr>
                )}
                {rowWindow.visibleItems.map(j => {
                  const d = jobData.get(j.id);
                  if (!d) return null;
                  const { progress, blockers, nextAction } = d;
                  const formattedAddress = formatAddress(j.address);

                  return (
                    <tr key={j.id} className={`border-b last:border-0 hover:bg-muted/30 transition-colors ${
                      progress.status === "blocked" ? "bg-destructive/[0.02]" : ""
                    }`}>
                      {/* Job ID */}
                      <td className="py-2.5 px-4">
                        <Link to={`/jobs/${j.id}`} className="text-primary font-medium hover:underline">{j.id}</Link>
                      </td>

                      {/* Customer */}
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Link to={`/customers/${encodeURIComponent(j.customerName)}`} className="font-medium hover:text-primary transition-colors">{j.customerName}</Link>
                          <ReadinessBadge job={j} />
                        </div>
                        <p
                          className="text-[10px] text-muted-foreground hidden sm:block truncate max-w-[220px]"
                          title={formattedAddress}
                        >
                          {formattedAddress}
                        </p>
                      </td>

                      {/* Stage */}
                      <td className="py-2.5 px-4">
                        <StatusBadge type="stage" value={j.currentStage} />
                      </td>

                      {/* Progress */}
                      <td className="py-2.5 px-4">
                        <ProgressCell progress={progress} />
                      </td>

                      {/* Blockers */}
                      <td className="py-2.5 px-4 hidden md:table-cell">
                        <BlockerCell blockers={blockers} isCompleted={j.currentStage === "Completed"} />
                      </td>

                      {/* Next Action — action-first: clickable button on every non-healthy state */}
                      <td className="py-2.5 px-4 hidden md:table-cell">
                        {nextAction ? (
                          <button
                            type="button"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              handleActionClick(j, nextAction.action);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15 transition-colors text-[10px] font-semibold whitespace-nowrap"
                            title={nextAction.action.description}
                          >
                            <nextAction.icon className="h-3 w-3 shrink-0" />
                            <span className="truncate max-w-[140px]">{nextAction.action.cta || nextAction.action.label}</span>
                            <ArrowRight className="h-2.5 w-2.5 shrink-0" />
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] text-success font-medium">
                            <CheckCircle2 className="h-3 w-3" /> On track
                          </span>
                        )}
                      </td>

                      {/* Assigned */}
                      <td className="py-2.5 px-4 hidden lg:table-cell">
                        <span className="text-muted-foreground">{j.assignedCrew || j.assignedSubcontractor || "—"}</span>
                      </td>

                      {/* Last Activity */}
                      <td className="py-2.5 px-4 hidden lg:table-cell">
                        {(() => {
                          const last = getLastActivity(j);
                          if (!last) return <span className="text-[10px] text-muted-foreground">—</span>;
                          return (
                            <div>
                              <span className="text-[10px] text-foreground font-medium">{formatRelativeTime(last.date)}</span>
                              <p className="text-[9px] text-muted-foreground truncate max-w-[100px]">{last.user}</p>
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
                {rowWindow.bottomSpacerHeight > 0 && (
                  <tr aria-hidden>
                    <td colSpan={8} style={{ height: rowWindow.bottomSpacerHeight, padding: 0, border: 0 }} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <EmptyState
              icon={Briefcase}
              title="No jobs yet"
              description="Jobs appear here once an opportunity is closed-won and ready for execution."
              actionLabel="View Opportunities"
              onAction={() => navigate("/opportunities")}
            />
          )}
          <PaginationBar {...pagination} />
        </CardContent>
      </Card>

      {/* Push-Mode chained modals — opened via usePushChain. Each one renders
          its own PushNextStepPanel on success; chain.chainTo handles the
          atomic close→open transition (no toast, no perceptible gap). */}
      {chain.ordering && (
        <OrderingModal
          open={!!chain.ordering}
          onOpenChange={(open) => { if (!open) chain.closeAll(); }}
          job={chain.ordering}
          openIssues={issues.filter(i => i.jobId === chain.ordering!.id && (i.status === "Open" || i.status === "In Progress"))}
          onSendToOrdering={handleOrderingSubmit}
          onChooseNextStep={(step) => {
            const j = jobs.find(x => x.id === chain.ordering!.id) ?? chain.ordering!;
            chain.chainTo(step, { ...j, materialReadiness: "Ordered" as Job["materialReadiness"] });
          }}
        />
      )}
      <RescheduleModal
        open={!!chain.reschedule}
        onOpenChange={(open) => { if (!open) chain.closeAll(); }}
        job={chain.reschedule}
        openIssues={chain.reschedule ? issues.filter(i => i.jobId === chain.reschedule!.id && (i.status === "Open" || i.status === "In Progress")) : []}
        onChooseNextStep={(step, updatedJob) => chain.chainTo(step, updatedJob)}
      />
      <QuickAssignModal
        open={!!chain.assign}
        onOpenChange={(open) => { if (!open) chain.closeAll(); }}
        job={chain.assign}
        openIssues={chain.assign ? issues.filter(i => i.jobId === chain.assign!.id && (i.status === "Open" || i.status === "In Progress")) : []}
        onChooseNextStep={(step, updatedJob) => chain.chainTo(step, updatedJob)}
      />
      <QuickResolveIssueModal
        open={!!chain.resolve}
        onOpenChange={(open) => { if (!open) chain.closeAll(); }}
        issue={chain.resolve}
        onChooseNextStep={(step, job) => chain.chainTo(step, job)}
      />
    </div>
  );
}

// ── KPI Card ──

function KpiCard({ label, value, sub, icon: Icon, color }: {
  label: string; value: string | number; sub: string; icon: typeof CheckCircle2; color: string;
}) {
  return (
    <div className={`rounded-xl border p-3 text-center ${color}`}>
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <p className="text-xl font-bold leading-none">{value}</p>
      <p className="text-[9px] font-semibold uppercase tracking-wider mt-0.5 opacity-70">{label}</p>
      <p className="text-[9px] opacity-50 mt-0.5">{sub}</p>
    </div>
  );
}

// ── Progress Cell ──

function ProgressCell({ progress }: { progress: JobProgress }) {
  const colorClass =
    progress.status === "completed" ? "bg-success" :
    progress.status === "blocked" ? "bg-destructive" :
    progress.status === "ready" ? "bg-success" :
    progress.percent >= 50 ? "bg-warning" :
    "bg-warning";

  const labelColor =
    progress.status === "blocked" ? "text-destructive" :
    progress.status === "ready" || progress.status === "completed" ? "text-success" :
    "text-warning";

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className={`text-[10px] font-semibold ${labelColor}`}>{progress.label}</span>
        <span className="text-[10px] font-bold text-foreground">{progress.percent}%</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${colorClass}`}
          style={{ width: `${Math.max(progress.percent, 2)}%` }}
        />
      </div>
      <div className="flex gap-1">
        {progress.milestones.map(m => (
          <span key={m.label} className={`text-[8px] ${m.done ? "text-success" : "text-muted-foreground/50"}`}>
            {m.done ? "●" : "○"} {m.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Blocker Cell ──

function BlockerCell({ blockers, isCompleted }: { blockers: Blocker[]; isCompleted: boolean }) {
  if (isCompleted) return <span className="text-[10px] text-muted-foreground">—</span>;
  if (blockers.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-success font-medium">
        <ShieldCheck className="h-3 w-3" /> Clear
      </span>
    );
  }

  // Show top 2 blockers with category icons
  return (
    <div className="space-y-0.5">
      {blockers.slice(0, 2).map((b, i) => {
        const Icon = BLOCKER_ICONS[b.category] || AlertTriangle;
        return (
          <div key={i} className={`flex items-center gap-1 text-[9px] font-medium ${
            b.severity === "critical" ? "text-destructive" :
            b.severity === "warning" ? "text-warning" :
            "text-muted-foreground"
          }`}>
            <Icon className="h-3 w-3 shrink-0" />
            <span className="truncate max-w-[120px]">{b.label}</span>
          </div>
        );
      })}
      {blockers.length > 2 && (
        <span className="text-[9px] text-muted-foreground">+{blockers.length - 2} more</span>
      )}
    </div>
  );
}
